/**
 * Feature availability hook.
 *
 * Reads /api/features once per session and exposes a map of
 * { [feature_key]: { enabled, label, reason, missing_fields } }.
 *
 * Pages call `useFeature(key)` to decide whether to render or show
 * the FeatureLocked panel. The sidebar uses this map to dim disabled
 * nav items.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { useAuth } from "./auth";

const FeaturesContext = createContext({ features: {}, capability: {}, loading: true, refresh: async () => {} });

export function FeaturesProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({ features: {}, capability: {}, loading: true });

  const refresh = useCallback(async () => {
    if (!user) {
      setState({ features: {}, capability: {}, loading: false });
      return;
    }
    try {
      const { data } = await api.get("/features");
      setState({ features: data.features || {}, capability: data.capability || {}, loading: false });
    } catch {
      setState({ features: {}, capability: {}, loading: false });
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <FeaturesContext.Provider value={{ ...state, refresh }}>
      {children}
    </FeaturesContext.Provider>
  );
}

export function useFeatures() {
  return useContext(FeaturesContext);
}

export function useFeature(key) {
  const { features, loading } = useFeatures();
  const f = features[key];
  return { loading, enabled: f?.enabled ?? true, reason: f?.reason || "", label: f?.label || key, missing: f?.missing_fields || [] };
}
